import { useState, useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { UseFormSetValue } from "react-hook-form";
import { queryCommonFlags } from "@/api/constants";
import { debounce } from "lodash";
import { getProperties } from "@/modules/tasks/api";
import { getPropertyOptionsData } from "./options";
import {
  PropertyOption,
  AddTaskFormFields,
  LinkToOptionsType,
  LinkedEntityParams,
} from "@/modules/tasks/types";
import { LinkToOptions } from "@/modules/tasks/enums";

export function useFetchProperties(
  open: boolean,
  linkTo: LinkToOptionsType | null,
  setValueAddTask: UseFormSetValue<AddTaskFormFields>,
  createLinkedEntity: (params: LinkedEntityParams) => any,
  userUId?: string
) {
  const [propertiesSearchTerm, setPropertiesSearchTerm] = useState<string>("");
  const [selectedProperty, setSelectedProperty] =
    useState<PropertyOption | null>(null);

  // Reset selected property when assignee is removed
  useEffect(() => {
    if (!userUId) {
      setSelectedProperty(null);
    }
  }, [userUId]);

  const { data: propertiesData, isLoading: propertiesLoading } = useQuery({
    queryKey: ["task-properties-search", propertiesSearchTerm, userUId],
    queryFn: () => getProperties(propertiesSearchTerm, userUId),
    ...queryCommonFlags,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    enabled: open && linkTo === LinkToOptions.PROPERTY,
    keepPreviousData: true,
  });

  const handlePropertiesSearch = useMemo(
    () =>
      debounce((searchTerm: string) => {
        setPropertiesSearchTerm(searchTerm);
      }, 300),
    []
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      handlePropertiesSearch.cancel();
    };
  }, [handlePropertiesSearch]);

  const propertyOptions = (() => {
    const rawPropertyData = getPropertyOptionsData(propertiesData);
    return rawPropertyData.map((option) => ({
      id: option.value,
      uid: option.uid,
      compound: option.compound || "Compound not found",
    }));
  })();

  // ===== PROPERTY SELECTION HANDLER =====
  const handlePropertySelection = useCallback(
    (
      linkTo: LinkToOptionsType,
      property: PropertyOption | null,
      fieldOnChange: (value: any[]) => void
    ) => {
      if (!property) {
        fieldOnChange([]);
        return;
      }

      const linkedEntity = createLinkedEntity({
        name: property.compound || property.label,
        systemUId: property.uid,
        id: property.id,
        type: linkTo,
      });

      setValueAddTask("linkedEntities", [linkedEntity]);
      fieldOnChange([linkedEntity]);
    },
    [setValueAddTask, createLinkedEntity]
  );

  const handlePropertyChange = useCallback(
    (
      linkTo: LinkToOptionsType,
      property: PropertyOption | null,
      fieldOnChange: (value: any[]) => void
    ) => {
      setSelectedProperty(property);
      handlePropertySelection(linkTo, property, fieldOnChange);
    },
    [setSelectedProperty, handlePropertySelection]
  );

  return {
    propertiesSearchTerm,
    setPropertiesSearchTerm,
    propertiesData,
    propertyOptions,
    handlePropertiesSearch,
    handlePropertyChange,
    selectedProperty,
    isLoading: propertiesLoading,
  };
}
